import { Product } from './product';
import { PRICE_IDS, PRICES } from '../shared/catalog';

// ---------------------------------------------------------------------------
// One shared shape + one builder for every product, regardless of which data
// source it came from. To add products you only edit a data table below; the
// id/name/size/fit-note/image/sold-out logic all lives in buildProduct().
// ---------------------------------------------------------------------------

type Entry = {
  key: string;              // stable identifier -> product id + display name
  size: string;             // may embed a fit note, e.g. "L...fits more like a small"
  price?: string;
  priceId?: string;         // Stripe Price ID; when absent the item renders sold out
  front: string;            // image path
  back?: string;            // optional second image
  releaseRank?: number;     // higher = newer in "Date, new to old" sort
  thumbFit?: 'cover' | 'contain';
};

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function prettyName(s: string) {
  return s.replace(/[-_]/g, ' ');
}

// Split a size string into its size code and an optional fit note. Handles both
// "L" and "L...fits more like a small" / "XL...fits like a medium ...".
function parseSize(sizeStr: string): { size: string; fitNote?: string } {
  const str = String(sizeStr);
  const parts = str.split(/\.\.\.|-|–|—/);
  const size = parts[0].trim().toUpperCase();
  const fitMatch = /fits\s+.*$/i.exec(str);
  let fitNote: string | undefined;
  if (fitMatch) fitNote = fitMatch[0].replace(/^[Ff]its\s*/, 'Fits ');
  else if (parts.length > 1) fitNote = parts.slice(1).join('...').trim();
  return { size, fitNote };
}

function buildProduct(e: Entry): Product {
  const { size, fitNote } = parseSize(e.size);
  const images = e.back ? [e.front, e.back] : [e.front];
  return {
    id: slugify(`${e.key}-${size}`),
    name: `${prettyName(e.key)} (${size})`,
    price: e.price,
    image: e.front,
    images,
    description: 'Hand-printed thrifted garment.',
    // No Stripe price yet => render as sold out so checkout can't break.
    soldOut: !e.priceId,
    priceId: e.priceId,
    sizes: [size],
    size,
    fitNote,
    ...(e.thumbFit ? { thumbFit: e.thumbFit } : {}),
    ...(e.releaseRank !== undefined ? { releaseRank: e.releaseRank } : {}),
  } as Product;
}

function garmentImage(stem: string, side: 'FRONT' | 'BACK', ext = 'webp') {
  return `/garments/${stem}-${side}.${ext}`;
}

// ---------------------------------------------------------------------------
// Data source 1: base catalog (KALI / HALLOW / PUNK-ALIEN tees & crews).
// Prices + Stripe IDs live in the shared catalog.json (also used by the
// checkout API). Sizes may include a fit note. Images are dashed .webp.
// ---------------------------------------------------------------------------
const SIZES: Record<string, string[]> = {
  '3-OM-KALI-TEE': ['L'],
  'AMBER-KIDS-TEE': ['M'],
  'BABYBLUE-LIL-DUDE-TEE': ['L...fits more like a small'],
  'BLUE-KALI-CREW': ['L'],
  'HEATHERBLUE-KIDS-TEE': ['M'],
  'KIDS-TEE': ['L'],
  'LIL-DUDE-TEE': ['M'],
  'OM-KALI-TEE': ['L'],
  'PINK-KALI-TEE': ['L'],
  'RED-KALI-TEE': ['M'],
  'YELLOW-KALI-TEE': ['M'],
  'YELLOW-LIL-DUDE-TEE': ['L'],
  'BLUE-HALLOW-TEE': ['M'],
  'BLACK-HALLOW-TEE': ['L'],
  'GRAY-HALLOW-TEE': ['L...fits more like a medium'],
  'GRANDPA-HALLOW-TEE': ['M'],
  'GREEN-HALLOW-TEE': ['M'],
  'WHITE-HALLOW-TEE': ['XL'],
  'RED-HALLOW-TEE': ['L'],
  'BLACK-PINK-PUNK-ALIEN-CREW': ['S'],
  'BLUE-PUNK-ALIEN-CREW': ['S'],
  'GRAYGREEN-PUNK-ALIEN-TEE': ['L'],
  'LIGHTBLUE-PUNK-ALIEN-CREW': ['M'],
  'PINK-PUNK-ALIEN-TEE': ['XL...fits like a medium and has an irregularly large head hole'],
  'SMOKY-BLUE-PUNK-ALIEN-TEE': ['L'],
};

// Order here controls display order within equal release ranks.
const BASES = [
  // --- PUNK ALIEN TEES & CREWS (show first) ---
  'BLACK-PINK-PUNK-ALIEN-CREW',
  'BLUE-PUNK-ALIEN-CREW',
  'GRAYGREEN-PUNK-ALIEN-TEE',
  'LIGHTBLUE-PUNK-ALIEN-CREW',
  'PINK-PUNK-ALIEN-TEE',
  'SMOKY-BLUE-PUNK-ALIEN-TEE',
  // --- HALLOW TEES ---
  'BLUE-HALLOW-TEE',
  'BLACK-HALLOW-TEE',
  'GRAY-HALLOW-TEE',
  'GRANDPA-HALLOW-TEE',
  'GREEN-HALLOW-TEE',
  'WHITE-HALLOW-TEE',
  'RED-HALLOW-TEE',
  // --- OTHER TEES ---
  '3-OM-KALI-TEE',
  'AMBER-KIDS-TEE',
  'BABYBLUE-LIL-DUDE-TEE',
  'BLUE-KALI-CREW',
  'HEATHERBLUE-KIDS-TEE',
  'KIDS-TEE',
  'LIL-DUDE-TEE',
  'OM-KALI-TEE',
  'PINK-KALI-TEE',
  'RED-KALI-TEE',
  'YELLOW-KALI-TEE',
  'YELLOW-LIL-DUDE-TEE',
];

const baseEntries: Entry[] = BASES.flatMap(base =>
  (SIZES[base] || []).map(size => ({
    key: base,
    size,
    price: PRICES[base],
    priceId: PRICE_IDS[base],
    front: garmentImage(base, 'FRONT'),
    back: garmentImage(base, 'BACK'),
    releaseRank: base.includes('ALIEN') ? 4 : base.includes('HALLOW') ? 3 : undefined,
  }))
);

// ---------------------------------------------------------------------------
// Data source 2: NPC drop. Inline price/priceId/size, and explicit image paths
// (filenames here are irregular — mixed .jpg, a couple of typos — so they can't
// be derived from the name).
// ---------------------------------------------------------------------------
const NPC_CATALOG: { name: string; price: string; priceId: string; size: string; front: string; back?: string }[] = [
  { name: 'WHITE_NPC_TEE_1', price: '$20', priceId: 'price_1S5drnAjn47w34hqnEatDFXl', size: 'L', front: '/garments/WHITE-NPC-TEE-1-FRONT.jpg', back: '/garments/WHITE-NPC-TEE-1-BACK.jpg' },
  { name: 'SALMON_NPC_TEE', price: '$20', priceId: 'price_1S5drRAjn47w34hqY0juXMZJ', size: 'L', front: '/garments/SALMON-NPC-TEE-FRONT.jpg', back: '/garments/SALMON-NPC-TEE-BACK.jpg' },
  { name: 'YELLOW_NPC_TEE', price: '$20', priceId: 'price_1S5dqfAjn47w34hqBKHVUaik', size: 'S', front: '/garments/YELLOW-NPC-TEE-FRONT.jpg', back: '/garments/YELLOW-NPC-TEE-BACK.jpg' },
  { name: 'BLUE_NPC_TEE', price: '$20', priceId: 'price_1S5dq2Ajn47w34hqgSNS2Ae2', size: 'XL', front: '/garments/BLUE-NPC-TEE-FRONT.jpg', back: '/garments/BLUE-NPC-TEE-BACK.jpg' },
  { name: 'WHITE_NPC_TEE', price: '$20', priceId: 'price_1S5dpPAjn47w34hq7h2opSez', size: 'L', front: '/garments/WHITE-NPC-TEE-FRONT.jpg', back: '/garments/WHITE-NPC-TEE-BACK.jpg' },
  { name: 'ORANGE_NPC_TEE', price: '$20', priceId: 'price_1S5dp4Ajn47w34hq7Zo3OlxL', size: 'XL', front: '/garments/ORANGE-NPC-TEE-FRONT.jpg', back: '/garments/ORANG-NPC-TEE-BACK.jpg' },
  { name: 'AMERICA_NPC_TEE', price: '$20', priceId: 'price_1S5doeAjn47w34hq62Th8vvH', size: 'XL', front: '/garments/AMERICA-NPC-TEE-FRONT.jpg', back: '/garments/AMERICA-NPC-TEE-BACK.jpg' },
  { name: 'BLACK_NPC_TEE_2', price: '$20', priceId: 'price_1S5dntAjn47w34hq4slr5zGD', size: 'L', front: '/garments/BLACK-NPC-TEE-2-FRONT.jpg', back: '/garments/BLACK-NPC-TEE-2-BACK.jpg' },
  { name: 'BLACK_NPC_TEE_1', price: '$20', priceId: 'price_1S5dnAAjn47w34hqlBY9UevW', size: 'L', front: '/garments/BLACK-NPC-TEE-1-FRONT.jpg', back: '/garments/BLACK-NPC-TEE-1-BACK.jpg' },
  { name: 'GRAY_SLEEVE_NPC_TEE', price: '$20', priceId: 'price_1S5dmVAjn47w34hqO7hgGaKw', size: 'L', front: '/garments/GRAY-SLEEVE-NPC-TEE-FRONT.jpg', back: '/garments/GRAY-SLEEVE-NPC-TEE-BACK.jpg' },
  { name: 'BLACK_NPC_TEE', price: '$20', priceId: 'price_1S5djxAjn47w34hqm7T6NEKe', size: 'L', front: '/garments/BLACK-NPC-TEE-FRONT.jpg', back: '/garments/BLACK-NPC-TEE-BACK.jpg' },
  { name: 'MAROON_NPC_TEE', price: '$20', priceId: 'price_1S5dj7Ajn47w34hqOvVWvHlX', size: 'L', front: '/garments/MAROON-NPC-TEE-FRONT.jpg', back: '/garments/MAROON-NPC-TEE-BACK.jpg' },
  { name: 'BLUE_NPC_CREW', price: '$25', priceId: 'price_1S5diAAjn47w34hqhgjHCh3V', size: 'L', front: '/garments/BLUE-NPC-CREW-FRONT.jpg', back: '/garments/BLUE-NPC-CREW-BACK.jpg' },
];

const npcEntries: Entry[] = NPC_CATALOG.map(({ name, price, priceId, size, front, back }) => ({
  key: name,
  size,
  price,
  priceId,
  front,
  back,
  thumbFit: 'contain',
  releaseRank: 2,
}));

// ---------------------------------------------------------------------------
// Data source 3: newest drop (cut-out .webp photos). Image filenames are the
// name with underscores -> dashes, so they're derived.
// ---------------------------------------------------------------------------
const NEW_DROP_CATALOG: { name: string; price: string; priceId: string; size: string }[] = [
  { name: 'BLACK_ALIEN', price: '$20', priceId: 'price_1TtpiSAjn47w34hqrSkiMdeR', size: 'M' },
  { name: 'BLUE_BUDDHA', price: '$20', priceId: 'price_1TtpiuAjn47w34hqVeNCKT2J', size: 'L' },
  { name: 'GITA_THERMAL', price: '$20', priceId: 'price_1TtpjEAjn47w34hq6jKXFj3A', size: 'L' },
  { name: 'HALLOWEEN', price: '$20', priceId: 'price_1TtpjlAjn47w34hq1USqA4RA', size: 'L' },
  { name: 'JESUS_LOVES_ME', price: '$30', priceId: 'price_1TtpkBAjn47w34hqPRT0elWm', size: 'XL' },
  { name: 'RED_BUDDHA', price: '$20', priceId: 'price_1Ttpl4Ajn47w34hq0YhjSSEm', size: 'M' },
  { name: 'TIEDYE_KIDS', price: '$20', priceId: 'price_1TtplRAjn47w34hqZ0jPNUmX', size: 'L' },
  { name: 'YELLOW_KIDS_SWEATER', price: '$20', priceId: 'price_1TtpllAjn47w34hqVoFUIYpp', size: 'S' },
];

const newDropEntries: Entry[] = NEW_DROP_CATALOG.map(({ name, price, priceId, size }) => {
  const stem = name.replace(/_/g, '-');
  return {
    key: name,
    size,
    price,
    priceId,
    front: garmentImage(stem, 'FRONT'),
    back: garmentImage(stem, 'BACK'),
    releaseRank: 12, // newest drop -> shows first
  };
});

export const garmentProducts: Product[] = [
  ...baseEntries,
  ...npcEntries,
  ...newDropEntries,
].map(buildProduct);
